import React from 'react';
import { Link } from 'react-router-dom';
import { LICENCJA_PO_SLUGU, PROG_PROCENT, przedmiotyLicencji, arkuszPrzedmiotu } from '../data/licencje';
import JsonLd from '../seo/JsonLd';
import { breadcrumbJsonLd, faqJsonLd } from '../seo/siteMeta';
import Faq from './Faq';
import styles from '../css/modules/Nauka.module.css';

/**
 * Strona filarowa o samym egzaminie teoretycznym przed Lotniczą Komisją
 * Egzaminacyjną: przebieg, liczby pytań, progi i terminy.
 *
 * Liczby pytań i czasy pochodzą z tabeli Krajowego Banku Pytań publikowanej
 * przez ULC. To parametry organizacyjne, które urząd zmienia komunikatem -
 * dlatego strona wprost odsyła do aktualnego komunikatu LKE zamiast udawać,
 * że dane są niezmienne.
 */

const FAQ = [
  {
    question: 'Ile przedmiotów obejmuje egzamin teoretyczny na PPL(A)?',
    answer:
      'Dziewięć: prawo lotnicze, ogólna wiedza o statku powietrznym, osiągi i planowanie lotu, człowiek - możliwości i ograniczenia, meteorologia, nawigacja, procedury operacyjne, zasady lotu oraz łączność. Każdy przedmiot to osobny test, zaliczany niezależnie.',
  },
  {
    question: 'Jaki jest próg zaliczenia?',
    answer: `Do zaliczenia przedmiotu potrzeba ${PROG_PROCENT} procent poprawnych odpowiedzi. Pytanie pozostawione bez odpowiedzi liczy się jak błędne, więc nigdy nie warto zostawiać pustych pól.`,
  },
  {
    question: 'Ile razy mogę podchodzić do jednego przedmiotu?',
    answer:
      'Maksymalnie cztery razy. Dodatkowo wszystkie przedmioty trzeba zaliczyć w nie więcej niż sześciu sesjach egzaminacyjnych. Po wyczerpaniu limitu konieczne jest ponowne szkolenie teoretyczne.',
  },
  {
    question: 'W jakim czasie muszę zdać wszystkie przedmioty?',
    answer:
      'W ciągu 18 miesięcy, licząc od końca miesiąca kalendarzowego, w którym przystąpiłeś do egzaminu po raz pierwszy. To osobny termin niż ważność zdanej teorii.',
  },
  {
    question: 'Jak długo ważna jest zdana teoria?',
    answer:
      'Komplet zaliczonych egzaminów teoretycznych jest ważny 24 miesiące. W tym czasie trzeba przystąpić do egzaminu praktycznego, inaczej teorię zdaje się od nowa.',
  },
  {
    question: 'Czy egzamin jest po polsku?',
    answer:
      'Egzaminy na licencje turystyczne PPL(A) i PPL(H) prowadzone są w języku polskim, w oparciu o Krajowy Bank Pytań. Egzaminy zawodowe według ECQB bywają zdawane po angielsku.',
  },
  {
    question: 'Czy uczę się z oryginalnych pytań ULC?',
    answer:
      'Nie. Krajowy Bank Pytań nie jest udostępniany publicznie, a serwisy, które twierdzą inaczej, opierają się na relacjach zdających. Nasze pytania są autorskie i napisane pod program egzaminacyjny, z wyjaśnieniem i wskazaniem przepisu przy każdej odpowiedzi.',
  },
];

const EgzaminULC = () => {
  const ppl = LICENCJA_PO_SLUGU['ppl-a'];
  const przedmioty = przedmiotyLicencji(ppl);

  return (
    <div className={styles.wrap}>
      <JsonLd data={breadcrumbJsonLd([
        { name: 'Strona główna', path: '/' },
        { name: 'Egzamin teoretyczny w ULC', path: '/egzamin-teoretyczny-ulc' },
      ])} />
      <JsonLd data={faqJsonLd(FAQ)} />

      <nav className={styles.breadcrumbs}>
        <Link to="/">Strona główna</Link> / Egzamin teoretyczny w ULC
      </nav>

      <h1 className={styles.h1}>Egzamin teoretyczny w ULC - jak wygląda i co trzeba zaliczyć</h1>
      <p className={styles.lead}>
        Egzamin teoretyczny do licencji PPL(A) i LAPL(A) zdaje się przed Lotniczą Komisją
        Egzaminacyjną Urzędu Lotnictwa Cywilnego. To nie jeden test, tylko dziewięć osobnych,
        a każdy zalicza się niezależnie przy {PROG_PROCENT} procentach poprawnych odpowiedzi.
      </p>

      <div className={styles.akcje}>
        <Link className={styles.cta} to="/intro">Sprawdź się w darmowym teście</Link>
        <Link className={styles.ctaDrugi} to="/nauka/ppl-a">Ucz się przedmiotami</Link>
      </div>

      <div className={styles.fakty}>
        <div className={styles.fakt}>
          <p className={styles.faktEtykieta}>Liczba testów</p>
          <p className={styles.faktWartosc}>{przedmioty.length}</p>
        </div>
        <div className={styles.fakt}>
          <p className={styles.faktEtykieta}>Próg zaliczenia</p>
          <p className={styles.faktWartosc}>{PROG_PROCENT} procent</p>
        </div>
        <div className={styles.fakt}>
          <p className={styles.faktEtykieta}>Podejść do przedmiotu</p>
          <p className={styles.faktWartosc}>4</p>
        </div>
        <div className={styles.fakt}>
          <p className={styles.faktEtykieta}>Ważność zdanej teorii</p>
          <p className={styles.faktWartosc}>24 miesiące</p>
        </div>
      </div>

      <div className={styles.tresc}>
        <h2 className={styles.h2}>Przedmioty, liczba pytań i czas</h2>
        <p>
          Poniższe wartości odpowiadają tabeli Krajowego Banku Pytań publikowanej przez ULC.
          Urząd może je zmienić komunikatem, dlatego przed sesją sprawdź aktualny komunikat
          Lotniczej Komisji Egzaminacyjnej.
        </p>
      </div>

      <div className={styles.tabelaOtoczka}>
        <table className={styles.tabela}>
          <thead>
            <tr>
              <th scope="col">Przedmiot</th>
              <th scope="col">Pytań</th>
              <th scope="col">Czas</th>
              <th scope="col">Do zaliczenia</th>
            </tr>
          </thead>
          <tbody>
            {przedmioty.map(p => {
              const arkusz = arkuszPrzedmiotu(ppl, p.slug);
              if (!arkusz) return null;
              return (
                <tr key={p.slug}>
                  <th scope="row">
                    <Link to={`/nauka/ppl-a/${p.slug}`}>{p.nazwa}</Link>
                  </th>
                  <td>{arkusz.pytan}</td>
                  <td>{arkusz.minut} min</td>
                  <td>{Math.ceil((arkusz.pytan * PROG_PROCENT) / 100)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={styles.tresc}>
        <h2 className={styles.h2}>Dwa terminy, które łatwo pomylić</h2>
        <p>
          Pierwszy dotyczy zaliczania: wszystkie przedmioty musisz zdać w ciągu 18 miesięcy,
          licząc od końca miesiąca kalendarzowego, w którym przystąpiłeś do pierwszego egzaminu.
          Drugi dotyczy ważności: komplet zdanej teorii jest ważny 24 miesiące i w tym czasie
          trzeba przystąpić do egzaminu praktycznego.
        </p>

        <h2 className={styles.h2}>Egzaminy poza PPL i LAPL</h2>
        <p>
          Świadectwo kwalifikacji pilota samolotu ultralekkiego zdaje się jako jeden test
          obejmujący wszystkie przedmioty szkolenia. Uprawnienia operatora bezzałogowca w
          podkategoriach A1 i A3 potwierdza egzamin online na platformie ULC, a podkategoria A2
          wymaga dodatkowego egzaminu teoretycznego. W obu przypadkach próg to również{' '}
          {PROG_PROCENT} procent.
        </p>
        <p>
          Uwaga na zmianę stanu prawnego po stronie bezzałogowców: krajowe scenariusze
          standardowe NSTS przestały obowiązywać 31 grudnia 2025 roku, a w kategorii szczególnej
          stosuje się teraz europejskie scenariusze STS-01 i STS-02.
        </p>

        <h2 className={styles.h2}>Jak się przygotować</h2>
        <p>
          Egzamin sprawdza nie tylko wiedzę, ale i tempo: przy 28 pytaniach z prawa lotniczego
          na 45 minut wychodzi niecałe dwie minuty na pytanie, licząc z czytaniem treści. Dlatego
          poza trybem nauki dajemy tryb pojedynku, w którym czas na odpowiedź jest ograniczony,
          a błąd kosztuje. Ćwiczenie pod presją czasu wyłapuje te zagadnienia, które znasz
          teoretycznie, ale nie odruchowo.
        </p>
      </div>

      <h2 className={styles.h2}>Najczęstsze pytania</h2>
      <Faq items={FAQ} />

      <div className={styles.sasiedzi}>
        <Link to="/licencja-ppl-a" className={styles.sasiad}>Testy na PPL(A)</Link>
        <Link to="/licencja-lapl-a" className={styles.sasiad}>Testy na LAPL(A)</Link>
        <Link to="/swiadectwo-kwalifikacji-ul" className={styles.sasiad}>Świadectwo kwalifikacji UACP</Link>
        <Link to="/dron-a1-a3" className={styles.sasiad}>Egzamin na drona A1/A3</Link>
      </div>
    </div>
  );
};

export default EgzaminULC;
